/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import { logger, sanitizeError } from "../../utils/logger.js";

/**
 * @fileoverview AppointmentPaymentModal Component
 * @description Modal component for processing payments for scheduled appointments.
 * Supports both cash (store) payments via barcode generation and card payments
 * through OpenPay integration with 3D Secure (aligned with {@link PaymentModal}).
 * The modal guides users through a multi-step payment flow with validation,
 * error handling, and success/failure feedback.
 * @module modals/AppointmentPaymentModal
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useNavigate } from "react-router";
import Barcode from "react-barcode";
import CircularProgress from "@mui/material/CircularProgress";
import { handleBusinessEventRegister } from "../../logic/attendance";
import { createStoreCharge } from "../../logic/openpay/index.js";
import { useCardPaymentFlow } from "../../hooks/useCardPaymentFlow.js";
import {
  detectCardType,
  formatCardNumber,
  formatExpiryDate,
  validateExpiryDate,
} from "../../utils/payment/cardValidation.js";
import {
  formatCurrentDate,
  formatDueDate,
  splitFullName,
} from "../../utils/payment/paymentFormatting.js";
import { updateAppointment } from "../../logic/appointment/index.js";
import { ReactComponent as CloseIcon } from "../../assets/icons/PaymentModal/CloseIcon.svg";
import { ReactComponent as CashImage } from "../../assets/icons/PaymentModal/CashImage.svg";
import { ReactComponent as CardImage } from "../../assets/icons/PaymentModal/CardImage.svg";
import { ReactComponent as CopyClipboardIcon } from "../../assets/icons/PaymentModal/CopyClipboardIcon.svg";
import { ReactComponent as CheckmarkIcon } from "../../assets/icons/PaymentModal/CheckmarkIcon.svg";
import { ReactComponent as PaymentSuccessIcon } from "../../assets/icons/PaymentModal/PaymentSuccessIcon.svg";
import { ReactComponent as PaymentErrorIcon } from "../../assets/icons/PaymentModal/PaymentErrorIcon.svg";
import { ReactComponent as PaymentsIcon } from "../../assets/icons/PaymentModal/PaymentsIcon.svg";
import CountryCodeSelector from "../../components/CountryCodeSelector";
import {  getCountryCodeOptions  } from "../../data/selectOptions";
import {
  getInvoicePhoneDefaultsFromUser,
  getSelectedCountryCodeOption,
} from "../../utils/common.js";
import { useFieldValidation } from "../../hooks/useFieldValidation";

import styles from "./index.module.css";

/**
 * @typedef {Object} Event
 * @property {string} _id - Unique identifier for the event
 * @property {string} nombre - Event name
 * @property {number} [costoSocios] - Cost for member users
 * @property {number} [costoNoSocios] - Cost for non-member users
 */

/**
 * @typedef {Object} User
 * @property {string} _id - Unique identifier for the user
 * @property {string} nombre - User's full name
 * @property {string} correoElectronico - User's email address
 * @property {string} tipoDeUsuario - User type ("1"-"5", where "4" is non-member)
 */

/**
 * @typedef {Object} Appointment
 * @property {string} _id - Unique identifier for the appointment
 * @property {boolean} pagado - Whether the appointment has been paid
 * @property {string} [pagadoPor] - ID of user who paid
 */

/**
 * @typedef {Object} ParticipatingCompany
 * @property {Appointment[]} citasAgendadas - Array of scheduled appointments
 */

/**
 * @typedef {Object} CardFormData
 * @property {string} nombreTitular - Cardholder's name
 * @property {string} numeroTarjeta - Card number (formatted with spaces)
 * @property {string} fechaDeVencimiento - Expiry date in MM/YY format
 * @property {string} codigoDeSeguridad - CVV/Security code
 */

/**
 * Modal component for processing appointment payments.
 * Provides a multi-step payment flow supporting cash and card payment methods.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls modal visibility
 * @param {Function} props.onClose - Callback function when modal is closed
 * @param {Event} props.event - Event object containing event details and pricing
 * @param {ParticipatingCompany} props.participatingCompany - Company with scheduled appointments
 * @param {User} props.user - Current user object with profile information
 *
 * @example
 * <AppointmentPaymentModal
 *   open={isModalOpen}
 *   onClose={() => setIsModalOpen(false)}
 *   event={selectedEvent}
 *   participatingCompany={company}
 *   user={currentUser}
 * />
 *
 * @returns {React.ReactElement|null} The modal component or null if not open
 */
const AppointmentPaymentModal = ({
  open,
  onClose,
  event,
  participatingCompany,
  user,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const countryCodeOptions = getCountryCodeOptions(t);
  const [stepNumber, setStepNumber] = useState(1);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [cashPaymentLoading, setCashPaymentLoading] = useState(false);
  const [cashPaymentReference, setCashPaymentReference] = useState(null);
  const [cashPaymentAmount, setCashPaymentAmount] = useState(null);
  const [cashPaymentDueDate, setCashPaymentDueDate] = useState(null);
  const [cardPaymentLoading, setCardPaymentLoading] = useState(false);
  const [modalMessage, setModalMessage] = useState(null);
  const [popupWindow, setPopupWindow] = useState(null);
  const [pollingAttempts, setPollingAttempts] = useState(0);
  const shouldContinuePolling = useRef(true);
  const pollingTimeoutRef = useRef(null);

  const [formData, setFormData] = useState({
    // Step 2: Card fields if card payment method is selected
    nombreTitular: "",
    numeroTarjeta: "",
    fechaDeVencimiento: "",
    codigoDeSeguridad: "",
  });

  const [cardType, setCardType] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash"); // "cash" or "card"
  const [isCopied, setIsCopied] = useState(false);
  const [cardValidationError, setCardValidationError] = useState("");
  const [expiryValidationError, setExpiryValidationError] = useState("");
  const [hasRegistered, setHasRegistered] = useState(false);
  const [wantsInvoiceCash, setWantsInvoiceCash] = useState(false);
  const [phoneCountryCodeCash, setPhoneCountryCodeCash] = useState("");
  const [confirmPhoneCash, setConfirmPhoneCash] = useState("");
  const [wantsInvoiceCard, setWantsInvoiceCard] = useState(false);
  const [phoneCountryCodeCard, setPhoneCountryCodeCard] = useState("");
  const [confirmPhoneCard, setConfirmPhoneCard] = useState("");
  const { handleFieldChange, getErrorMessage, getFieldValidationState } =
    useFieldValidation({});

  /**
   * Copies text to the system clipboard and shows a temporary success indicator.
   * @async
   * @param {string} text - The text to copy to clipboard
   * @returns {Promise<void>}
   */
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
      logger.info("Copied to clipboard:", { data: text });
    } catch (err) {
      logger.error("Failed to copy: ", { err: sanitizeError(err) });
    }
  };

  /**
   * Detects the card type based on the card number prefix.
    // If we have 1 digit or less, just return the digits
    return digits;
  };

  /**
   * Validates Step 1 of the payment flow (payment method selection and card details).
   * For cash payments, no additional validation is needed.
   * For card payments, validates all card fields including number, expiry, and CVV.
   * @returns {boolean} True if step 1 is valid and user can proceed
   */
  const isStep1Valid = () => {
    // If no payment method is selected, step is not valid
    if (!paymentMethod) {
      return false;
    }

    // For cash payment, no additional validation needed
    if (paymentMethod === "cash") {
      return isInvoicePhoneValid("cash");
    }

    // For card payment, validate all card fields
    if (paymentMethod === "card") {
      const {
        nombreTitular,
        numeroTarjeta,
        fechaDeVencimiento,
        codigoDeSeguridad,
      } = formData;

      // Check if card fields exist before validation
      if (
        !numeroTarjeta ||
        !fechaDeVencimiento ||
        !codigoDeSeguridad ||
        !nombreTitular
      ) {
        return false;
      }

      const cardNumber = numeroTarjeta.replace(/\s/g, "");
      const detectedCardType = detectCardType(cardNumber);
      const expiryValidation = validateExpiryDate(fechaDeVencimiento);

      // Check if card number is long enough and card type is detected
      const isValidCardNumber =
        cardNumber.length >= 13 && detectedCardType !== "";

      return (
        isValidCardNumber &&
        nombreTitular.trim() !== "" &&
        fechaDeVencimiento.trim() !== "" &&
        expiryValidation.isValid &&
        codigoDeSeguridad.length >= 3 &&
        isInvoicePhoneValid("card")
      );
    }

    return false;
  };

  /**
   * Validates Step 2 of the payment flow (payment confirmation).
   * Currently always returns true as no additional validation is needed.
   * @returns {boolean} True if step 2 is valid
   */
  const isStep2Valid = () => {
    return true;
  };

  /**
   * Handles form input changes with special formatting for card-related fields.
   * Applies formatting for card number and expiry date, and validates input in real-time.
   * @param {string} field - The form field name to update
   * @param {string} value - The new value for the field
   */
  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });

    if (field === "numeroTarjeta") {
      const formatted = formatCardNumber(value);
      setFormData((prev) => ({ ...prev, [field]: formatted }));
      const detectedType = detectCardType(value);
      setCardType(detectedType);

      // Set validation error if card number is long enough but no type detected
      const cardNumber = value.replace(/\s/g, "");
      if (cardNumber.length >= 13 && detectedType === "") {
        setCardValidationError(
          t("components.paymentModal.invalidCardNumberFormat"),
        );
      } else {
        setCardValidationError("");
      }
    } else if (field === "fechaDeVencimiento") {
      const formatted = formatExpiryDate(value);
      setFormData((prev) => ({ ...prev, [field]: formatted }));

      // Validate expiry date and set error
      const validation = validateExpiryDate(formatted);
      if (!validation.isValid && formatted.length === 5) {
        setExpiryValidationError(
          t("components.paymentModal.invalidExpiryDateFormat"),
        );
      } else {
        setExpiryValidationError("");
      }
    } else {
      // Non-card fields use the default setFormData above
    }
  };

  const handleWantsInvoiceChange = (method, checked) => {
    const isCash = method === "cash";

    if (isCash) {
      setWantsInvoiceCash(checked);

      if (!checked) {
        setPhoneCountryCodeCash("");
        setConfirmPhoneCash("");
        handleFieldChange("confirmPhoneCash", "");
        return;
      }

      const { telefonoCodigoPais, telefono } =
        getInvoicePhoneDefaultsFromUser(user);

      setPhoneCountryCodeCash(
        (currentValue) => currentValue || telefonoCodigoPais,
      );
      setConfirmPhoneCash((currentValue) => currentValue || telefono);
      return;
    }

    setWantsInvoiceCard(checked);

    if (!checked) {
      setPhoneCountryCodeCard("");
      setConfirmPhoneCard("");
      handleFieldChange("confirmPhoneCard", "");
      return;
    }

    const { telefonoCodigoPais, telefono } =
      getInvoicePhoneDefaultsFromUser(user);

    setPhoneCountryCodeCard(
      (currentValue) => currentValue || telefonoCodigoPais,
    );
    setConfirmPhoneCard((currentValue) => currentValue || telefono);
  };

  const handleConfirmPhoneChange = (method, value) => {
    const validationField =
      method === "cash" ? "confirmPhoneCash" : "confirmPhoneCard";

    if (method === "cash") {
      setConfirmPhoneCash(value);
    } else {
      setConfirmPhoneCard(value);
    }

    handleFieldChange(validationField, value, {
      required: true,
      pattern: /^[\d\s]+$/,
      patternDescription: "PHONE_MUST_BE_10_DIGITS",
      customValidation: (phoneValue) => {
        if (!phoneValue || phoneValue.trim() === "") {
          return {
            isValid: false,
            error: "FIELD_REQUIRED",
          };
        }

        const phoneDigitsOnly = phoneValue.replace(/\s/g, "");
        if (!/^\d{10}$/.test(phoneDigitsOnly)) {
          return {
            isValid: false,
            error: "PHONE_MUST_BE_10_DIGITS",
          };
        }

        return { isValid: true };
      },
    });
  };

  const isInvoicePhoneValid = (method) => {
    const isCash = method === "cash";
    const wantsInvoice = isCash ? wantsInvoiceCash : wantsInvoiceCard;
    const countryCode = isCash ? phoneCountryCodeCash : phoneCountryCodeCard;
    const phone = isCash ? confirmPhoneCash : confirmPhoneCard;
    const validationField = isCash ? "confirmPhoneCash" : "confirmPhoneCard";

    if (!wantsInvoice) {
      return true;
    }

    if (!countryCode?.trim() || !phone?.trim()) {
      return false;
    }

    const digitsOnly = phone.replace(/\s/g, "");
    const isValidPhone = /^\d{10}$/.test(digitsOnly);
    const hasPhoneValidationError = Boolean(getErrorMessage(validationField));

    return isValidPhone && !hasPhoneValidationError;
  };

  /**
   * Gets the appropriate event cost based on the user's membership type.
   * Returns member pricing for user types 1, 2, 3, 5 and non-member pricing for type 4.
   * @returns {number} The event cost in MXN
   */
  const getEventCost = () => {
    if (!event) {
      return 0;
    }
    if (user?.tipoDeUsuario === "4") {
      return Number(event.costoNoSocios) || 0;
    }
    if (["1", "2", "3", "5"].includes(user?.tipoDeUsuario)) {
      return Number(event.costoSocios) || 0;
    }
    return 0; // Unknown user type — no event cost applies
  };

  /**
   * Handles cash/store payment processing.
   * Creates a store charge through OpenPay which generates a barcode for cash payment.
   * On success, moves to the next step to display the payment barcode.
   * @async
   * @returns {Promise<void>}
   */
  const handleCashPayment = async () => {
    if (!isStep1Valid()) {
      return;
    }

    setCashPaymentLoading(true);
    try {
      const { userName, userLastName } = splitFullName(user.nombre);

      const storeChargeData = {
        eventInformation: {
          eventId: event._id,
          eventName: event.nombre,
          appointmentId: participatingCompany?.citasAgendadas?.[0]?._id,
        },
        customerInformation: {
          userEmail: user.correoElectronico,
          userName: userName,
          userLastName: userLastName,
          quiereFactura: wantsInvoiceCash,
          telefonoCodigoPais: wantsInvoiceCash ? phoneCountryCodeCash : null,
          telefono: wantsInvoiceCash ? confirmPhoneCash : null,
        },
        paymentInformation: {
          amount: getEventCost(),
          description: "Store charge for event registration",
        },
      };

      const data = await createStoreCharge(user._id, storeChargeData, navigate);

      if (data) {
        logger.info("Store charge created successfully:", { data: data });
        setCashPaymentReference(data.storeChargeCreated.barcode.reference);
        setCashPaymentAmount(data.storeChargeCreated.amount);
        setCashPaymentDueDate(data.storeChargeCreated.dueDate);
        setStepNumber(stepNumber + 1);
      } else {
        logger.error("Failed to create store charge");
      }
    } catch (error) {
      logger.error("Error creating store charge:", {
        err: sanitizeError(error),
      });
    } finally {
      setCashPaymentLoading(false);
    }
  };

  const buildCardChargeData = useCallback(
    ({ tokenResponse, deviceSessionId, cardNumber }) => {
      const { userName, userLastName } = splitFullName(formData.nombreTitular);
      const redirectUrl = `${window.location.origin}/payment/3ds-callback`;

      return {
        eventInformation: {
          eventId: event._id,
          eventName: event.nombre,
          appointmentId: participatingCompany?.citasAgendadas?.[0]?._id,
        },
        customerInformation: {
          userEmail: user.correoElectronico,
          userName,
          userLastName,
          quiereFactura: wantsInvoiceCard,
          telefonoCodigoPais: wantsInvoiceCard ? phoneCountryCodeCard : null,
          telefono: wantsInvoiceCard ? confirmPhoneCard : null,
        },
        paymentInformation: {
          amount: getEventCost(),
          description: "Card charge for event registration",
          cardToken: tokenResponse.id,
          deviceSessionId,
          cardLastDigits: cardNumber.slice(-4),
          redirectUrl,
        },
      };
    },
    [
      confirmPhoneCard,
      event._id,
      event.nombre,
      formData.nombreTitular,
      participatingCompany?.citasAgendadas,
      phoneCountryCodeCard,
      user.correoElectronico,
      wantsInvoiceCard,
    ],
  );

  const { handleCardPayment } = useCardPaymentFlow({
    resultStepNumber: 3,
    stepNumber,
    setStepNumber,
    setPaymentStatus,
    setCardPaymentLoading,
    setModalMessage,
    setPopupWindow,
    setPollingAttempts,
    shouldContinuePolling,
    pollingTimeoutRef,
    navigate,
    t,
    user,
    formData,
    validateBeforePayment: isStep2Valid,
    buildCardChargeData,
  });

  /**
   * Marks the appointment as paid after a successful card payment.
   * The modal stays open until the user closes it manually.
   * @async
   * @returns {Promise<void>}
   */
  const handleRegister = async () => {
    const currentAppointment = participatingCompany?.citasAgendadas?.[0];
    if (!currentAppointment?._id || !event?._id) {
      logger.error("Failed to get current appointment");
      return;
    }

    try {
      const appointmentData = {
        pagado: true,
        pagadoPor: user?._id,
      };
      logger.info("Updating appointment");
      const updatedAppointment = await updateAppointment(
        navigate,
        currentAppointment._id,
        event._id,
        { appointmentInformation: appointmentData },
      );

      if (!updatedAppointment) {
        logger.error("Failed to update appointment");
      }
    } catch (error) {
      logger.error("Error:", { err: sanitizeError(error) });
    }
  };

  // Load OpenPay SDK dynamically
  useEffect(() => {
    const loadOpenPaySDK = () => {
      // Check if OpenPay is already loaded
      if (window.OpenPay) {
        return Promise.resolve();
      }

      return new Promise((resolve, reject) => {
        // Load main OpenPay script
        const script = document.createElement("script");
        script.src = "https://js.openpay.mx/openpay.v1.min.js";
        script.async = true;
        script.onload = () => {
          // Load OpenPay data script
          const dataScript = document.createElement("script");
          dataScript.src = "https://js.openpay.mx/openpay-data.v1.min.js";
          dataScript.async = true;
          dataScript.onload = () => resolve();
          dataScript.onerror = () =>
            reject(new Error("Failed to load OpenPay data script"));
          document.body.appendChild(dataScript);
        };
        script.onerror = () =>
          reject(new Error("Failed to load OpenPay script"));
        document.body.appendChild(script);
      });
    };

    if (open) {
      loadOpenPaySDK().catch((error) => {
        logger.error("Error loading OpenPay SDK:", {
          err: sanitizeError(error),
        });
      });
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      shouldContinuePolling.current = false;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      if (popupWindow && !popupWindow.closed) {
        popupWindow.close();
      }
      setPopupWindow(null);
      setModalMessage(null);
    } else {
      shouldContinuePolling.current = true;
    }
  }, [open, popupWindow]);

  useEffect(() => {
    if (stepNumber !== 2 && stepNumber !== 3) {
      shouldContinuePolling.current = false;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      setModalMessage(null);
    } else {
      shouldContinuePolling.current = true;
    }
  }, [stepNumber]);

  useEffect(() => {
    if (open && stepNumber === 1) {
      setPaymentMethod("cash");
      setCardValidationError("");
      setExpiryValidationError("");
      setHasRegistered(false);
      setModalMessage(null);
      setPollingAttempts(0);
      setWantsInvoiceCash(false);
      setPhoneCountryCodeCash("");
      setConfirmPhoneCash("");
      setWantsInvoiceCard(false);
      setPhoneCountryCodeCard("");
      setConfirmPhoneCard("");
      handleFieldChange("confirmPhoneCash", "");
      handleFieldChange("confirmPhoneCard", "");

      setFormData((prev) => ({
        ...prev,
        nombreTitular: "",
        numeroTarjeta: "",
        fechaDeVencimiento: "",
        codigoDeSeguridad: "",
      }));
    }
  }, [open, stepNumber]);

  // Register user to event when payment is successful
  useEffect(() => {
    if (
      stepNumber === 3 &&
      paymentMethod === "card" &&
      paymentStatus === "PAYMENT_SUCCESS" &&
      !hasRegistered
    ) {
      setHasRegistered(true);
      handleRegister();
    }
  }, [stepNumber, paymentMethod, paymentStatus, hasRegistered]);

  if (!open) {
    return null;
  }

  const renderConfirmPhoneField = (labelClassName, method) => {
    const isCash = method === "cash";
    const phoneCountryCode = isCash
      ? phoneCountryCodeCash
      : phoneCountryCodeCard;
    const confirmPhone = isCash ? confirmPhoneCash : confirmPhoneCard;
    const validationField = isCash ? "confirmPhoneCash" : "confirmPhoneCard";

    return (
      <>
        <label className={labelClassName}>
          {t("components.paymentModal.cashSection1ConfirmPhone")}
        </label>
        <div className={styles.phoneInputContainer}>
          <CountryCodeSelector
            value={getSelectedCountryCodeOption(
              countryCodeOptions,
              phoneCountryCode,
            )}
            onChange={(value) => {
              if (isCash) {
                setPhoneCountryCodeCash(value.value);
              } else {
                setPhoneCountryCodeCard(value.value);
              }
            }}
          />
          <input
            type="text"
            className={`${styles.phoneInput} ${
              getFieldValidationState(validationField, confirmPhone || "") ===
              "error"
                ? styles.inputError
                : ""
            }`}
            placeholder={t(
              "components.contactInformation.officePhonePlaceholder",
            )}
            value={confirmPhone || ""}
            onChange={(e) => handleConfirmPhoneChange(method, e.target.value)}
            style={{
              borderColor:
                getFieldValidationState(validationField, confirmPhone || "") ===
                "error"
                  ? "#FF0000"
                  : "#909092",
            }}
          />
        </div>
        {getErrorMessage(validationField) && (
          <p className={styles.errorMessage}>
            {getErrorMessage(validationField)}
          </p>
        )}
      </>
    );
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContainer}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.headerTop}>
            <button className={styles.closeButton} onClick={onClose}>
              <CloseIcon />
            </button>
          </div>
          {stepNumber === 1 && (
            <div className={styles.modalTitle}>
              <div className={styles.title}>
                {t("components.paymentModal.title")}
              </div>
              <p className={styles.subtitle}>
                {t("components.paymentModal.subtitle")}
              </p>
            </div>
          )}
        </div>

        {/* Step 2: Payment Information (Choose payment method) */}
        {stepNumber === 1 && (
          <div className={styles.stepContent}>
            {/* Payment Method Selection */}
            <div className={styles.formRow}>
              <div className={styles.formColumn}>
                <div className={styles.paymentMethodButtons}>
                  <button
                    className={`${styles.paymentMethodButton} ${
                      paymentMethod === "cash" ? styles.activeButton : ""
                    }`}
                    onClick={() => setPaymentMethod("cash")}
                  >
                    {t("components.paymentModal.cashSection1Title")}
                  </button>
                  <button
                    className={`${styles.paymentMethodButton} ${
                      paymentMethod === "card" ? styles.activeButton : ""
                    }`}
                    onClick={() => setPaymentMethod("card")}
                  >
                    {t("components.paymentModal.paymentMethodCard")}
                  </button>
                </div>
              </div>
            </div>

            {/* Cash Payment Content */}
            {paymentMethod === "cash" && (
              <div className={styles.cashPaymentContent}>
                <div className={styles.cashPaymentTitleContainer}>
                  <div className={styles.cashPaymentTitle}>
                    {t("components.paymentModal.cashSection1Title")}
                  </div>
                  <p className={styles.cashPaymentSubtitle}>
                    {t("components.paymentModal.cashSection1Description")}
                  </p>
                </div>
                <div className={styles.cashPaymentInstructions}>
                  <p className={styles.cashPaymentInstruction}>
                    1.{" "}
                    <Trans
                      i18nKey="components.paymentModal.cashSection1Instruction1"
                      components={{ bold: <strong /> }}
                    />
                  </p>
                  <p className={styles.cashPaymentInstruction}>
                    2. {t("components.paymentModal.cashSection1Instruction2")}
                  </p>
                  <p className={styles.cashPaymentInstruction}>
                    3. {t("components.paymentModal.cashSection1Instruction3")}
                  </p>
                </div>
                <div className={styles.cashPaymentInvoiceSection}>
                  <div className={styles.invoiceCheckboxContainer}>
                    <input
                      type="checkbox"
                      id="wantsInvoiceCash"
                      className={styles.invoiceCheckbox}
                      checked={wantsInvoiceCash}
                      onChange={(e) =>
                        handleWantsInvoiceChange("cash", e.target.checked)
                      }
                    />
                    <label
                      htmlFor="wantsInvoiceCash"
                      className={styles.invoiceCheckboxLabel}
                    >
                      {t("components.paymentModal.cashSection1GenerateInvoice")}
                    </label>
                  </div>
                  {wantsInvoiceCash && (
                    <div className={styles.categoryContainer}>
                      {renderConfirmPhoneField(styles.label, "cash")}
                    </div>
                  )}
                </div>
                <CashImage className={styles.cashPaymentImage} />
              </div>
            )}

            {/* Card Payment Content */}
            {paymentMethod === "card" && (
              <div className={styles.cardPaymentContent}>
                <div className={styles.cardPaymentTitleContainer}>
                  <div className={styles.cardPaymentTitle}>
                    {t("components.paymentModal.cardSection1Title")}
                  </div>
                  <p className={styles.cardPaymentSubtitle}>
                    {t("components.paymentModal.cardSection1Description")}
                  </p>
                </div>

                <div className={styles.cardPaymentForm}>
                  {/* First Row: Cardholder Name and Card Number */}
                  <div className={styles.formRow}>
                    <div className={styles.formColumn}>
                      <div className={styles.formLabel}>
                        {t("components.paymentModal.cardSection1Label1")}
                      </div>
                      <input
                        type="text"
                        value={formData.nombreTitular}
                        onChange={(e) =>
                          handleInputChange("nombreTitular", e.target.value)
                        }
                        className={styles.formInput}
                        placeholder={t(
                          "components.paymentModal.cardSection1Placeholder1",
                        )}
                      />
                    </div>
                    <div className={styles.formColumn}>
                      <div className={styles.formLabel}>
                        {t("components.paymentModal.cardSection1Label2")}
                      </div>
                      <div className={styles.cardInputContainer}>
                        <input
                          type="text"
                          value={formData.numeroTarjeta}
                          onChange={(e) =>
                            handleInputChange("numeroTarjeta", e.target.value)
                          }
                          className={styles.cardInput}
                          placeholder={t(
                            "components.paymentModal.cardSection1Placeholder2",
                          )}
                          maxLength="19"
                        />
                        {cardType && (
                          <div
                            className={`${styles.cardType} ${styles[cardType]}`}
                          >
                            {cardType.toUpperCase()}
                          </div>
                        )}
                      </div>
                      {cardValidationError && (
                        <div className={styles.errorMessage}>
                          {cardValidationError}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Second Row: Expiry Date and CVV */}
                  <div className={styles.formRow}>
                    <div className={styles.formColumn}>
                      <div className={styles.formLabel}>
                        {t("components.paymentModal.cardSection1Label3")}
                      </div>
                      <div className={styles.expiryContainer}>
                        <input
                          type="text"
                          value={formData.fechaDeVencimiento}
                          onChange={(e) =>
                            handleInputChange(
                              "fechaDeVencimiento",
                              e.target.value,
                            )
                          }
                          className={styles.expiryInput}
                          placeholder={t(
                            "components.paymentModal.cardSection1Placeholder3",
                          )}
                          maxLength="5"
                        />
                      </div>
                      {expiryValidationError && (
                        <div className={styles.errorMessage}>
                          {expiryValidationError}
                        </div>
                      )}
                    </div>
                    <div className={styles.formColumn}>
                      <div className={styles.formLabel}>
                        {t("components.paymentModal.cardSection1Label4")}
                      </div>
                      <div className={styles.cvvContainer}>
                        <input
                          type="text"
                          value={formData.codigoDeSeguridad}
                          onChange={(e) =>
                            handleInputChange(
                              "codigoDeSeguridad",
                              e.target.value,
                            )
                          }
                          className={styles.cvvInput}
                          placeholder={t(
                            "components.paymentModal.cardSection1Placeholder4",
                          )}
                          maxLength="4"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Third Row: Invoice checkbox and optional phone */}
                  <div className={styles.formRow}>
                    <div
                      className={`${styles.formColumn} ${styles.cardInvoiceCheckboxColumn}`}
                    >
                      <div className={styles.cardInvoiceCheckboxContainer}>
                        <input
                          type="checkbox"
                          id="wantsInvoiceCard"
                          className={styles.invoiceCheckbox}
                          checked={wantsInvoiceCard}
                          onChange={(e) =>
                            handleWantsInvoiceChange("card", e.target.checked)
                          }
                        />
                        <label
                          htmlFor="wantsInvoiceCard"
                          className={styles.invoiceCheckboxLabel}
                        >
                          {t(
                            "components.paymentModal.cashSection1GenerateInvoice",
                          )}
                        </label>
                      </div>
                    </div>
                    {wantsInvoiceCard && (
                      <div className={styles.formColumn}>
                        {renderConfirmPhoneField(styles.formLabel, "card")}
                      </div>
                    )}
                  </div>

                  <div className={styles.cardImageContainer}>
                    <CardImage className={styles.cardPaymentImage} />
                    <div className={styles.paymentsIconContainer}>
                      <PaymentsIcon className={styles.paymentsIcon} />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Cash Payment Information (Barcode generation) */}
        {stepNumber === 2 && paymentMethod === "cash" && (
          <div className={styles.stepContent}>
            <div className={styles.cashPaymentStepContent}>
              <div className={styles.cashPaymentTitleContainer}>
                <div className={styles.cashPaymentTitle}>
                  {t("components.paymentModal.cashSection2Title")}
                </div>
                <p className={styles.cashPaymentSubtitle}>
                  {t("components.paymentModal.cashSection2Description")}
                </p>
              </div>
              <div className={styles.cashPaymentInformationContent}>
                <div className={styles.cashPaymentTitleContainer}>
                  <div className={styles.cashPaymentTitle}>
                    {t("components.paymentModal.cashSection2Subtitle")}
                  </div>
                  <p className={styles.cashPaymentInstructionsLabel}>
                    {t("components.paymentModal.cashSection2Instructions")}
                  </p>
                </div>
                <div className={styles.cashPaymentInstructions}>
                  <p className={styles.cashPaymentInstruction}>
                    1. {t("components.paymentModal.cashSection2Instruction1")}
                  </p>
                  <p className={styles.cashPaymentInstruction}>
                    2. {t("components.paymentModal.cashSection2Instruction2")}
                  </p>
                  <p className={styles.cashPaymentInstruction}>
                    3. {t("components.paymentModal.cashSection2Instruction3")}
                  </p>
                </div>

                {/* Barcode Section */}
                <div className={styles.barcodeSection}>
                  <Barcode
                    value={cashPaymentReference}
                    format="CODE128"
                    width={4}
                    height={110}
                  />
                  <div className={styles.barcodeNumberContainer}>
                    <span className={styles.barcodeNumber}>
                      {cashPaymentReference}
                    </span>
                    <button
                      className={`${styles.copyButton} ${
                        isCopied ? styles.copied : ""
                      }`}
                      onClick={() => copyToClipboard(cashPaymentReference)}
                      title={isCopied ? "Copied!" : "Copy to clipboard"}
                    >
                      {isCopied ? <CheckmarkIcon /> : <CopyClipboardIcon />}
                    </button>
                  </div>
                  <span className={styles.cashPaymentBarcodeNumberLabel}>
                    {t("components.paymentModal.cashSection2Label1")}
                  </span>
                </div>

                {/* Cash Payment Details Section */}
                <div className={styles.cashPaymentDetailsSection}>
                  <div className={styles.cashPaymentDetailRow}>
                    <span className={styles.cashPaymentDetailAmount}>
                      ${cashPaymentAmount} MXN
                    </span>
                  </div>
                  <div className={styles.cashPaymentDetailRow}>
                    <span className={styles.cashPaymentDetailLabel}>
                      {t("components.paymentModal.cashSection2Label2")}
                    </span>
                  </div>
                  <div className={styles.cashPaymentDateRow}>
                    <span className={styles.cashPaymentDetailLabel}>
                      {t("components.paymentModal.cashSection2Label3")}
                    </span>
                    <span className={styles.cashPaymentDetailValue}>
                      {formatDueDate(cashPaymentDueDate)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Card Payment Information (Payment details) */}
        {stepNumber === 2 && paymentMethod === "card" && (
          <div className={styles.stepContent}>
            <div className={styles.cardPaymentTitleContainer}>
              <div className={styles.cardPaymentTitle}>
                {t("components.paymentModal.cardSection2Title")}
              </div>
              <p className={styles.cardPaymentSubtitle}>
                {t("components.paymentModal.cardSection2Description")}
              </p>
            </div>
            <div className={styles.cardPaymentInformationContent}>
              {/* Card Payment Details Section */}
              <div className={styles.cardPaymentDetailsSection}>
                <div className={styles.cardPaymentDetailRow}>
                  <span className={styles.cardPaymentDetailLabel}>
                    {t("components.paymentModal.cardSection2Label1")}
                  </span>
                </div>
                <div className={styles.cardPaymentDetailRow}>
                  <span className={styles.cardPaymentDetailLabel}>
                    {t("components.paymentModal.cardSection2Label2")}
                  </span>
                  <span className={styles.cardPaymentDetailValue}>
                    {formatCurrentDate()}
                  </span>
                </div>
                <div className={styles.cardPaymentDetailRow}>
                  <span className={styles.cardPaymentDetailLabel}>
                    {t("components.paymentModal.cardSection2Label3")}
                  </span>
                  <span className={styles.cardPaymentDetailValue}>
                    {t("components.paymentModal.cardSection2Label8")}
                  </span>
                </div>
                <div className={styles.cardPaymentDetailRow}>
                  <span className={styles.cardPaymentDetailLabel}>
                    {t("components.paymentModal.cardSection2Label4")}
                  </span>
                  <span className={styles.cardPaymentDetailValue}>
                    **** **** ****{" "}
                    {formData.numeroTarjeta.replace(/\s/g, "").slice(-4)}
                  </span>
                </div>
                <div className={styles.cardPaymentDetailRow}>
                  <span className={styles.cardPaymentDetailLabel}>
                    {t("components.paymentModal.cardSection2Label5")}
                  </span>
                  <span className={styles.cardPaymentDetailValue}>
                    {formData.nombreTitular}
                  </span>
                </div>
                <div className={styles.cardPaymentDetailRow}>
                  <span className={styles.cardPaymentDetailLabel}>
                    {t("components.paymentModal.cardSection2Label6")}
                  </span>
                  <span className={styles.cardPaymentDetailValue}>
                    {user.correoElectronico}
                  </span>
                </div>
                <div className={styles.cardPaymentDivider}></div>
                <div className={styles.cardPaymentDetailRow}>
                  <span className={styles.cardPaymentDetailLabel}>
                    {t("components.paymentModal.cardSection2Label7")}
                  </span>
                  <span className={styles.cardPaymentDetailValue}>
                    ${getEventCost()} MXN
                  </span>
                </div>
              </div>
              {modalMessage && (
                <div className={styles.cardPaymentDetailsSection}>
                  <div className={styles.cardPaymentDetailRow}>
                    <span className={styles.cardPaymentDetailLabel}>
                      {modalMessage}
                    </span>
                    {cardPaymentLoading && (
                      <CircularProgress size={20} color="inherit" />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Payment Success */}
        {stepNumber === 3 &&
          paymentMethod === "card" &&
          paymentStatus === "PAYMENT_SUCCESS" && (
            <div className={styles.stepContent}>
              <div className={styles.cardPaymentTitleContainer}>
                <div className={styles.cardPaymentTitle}>
                  {t("components.paymentModal.cardSection3Title")}
                </div>
                <p className={styles.cardPaymentSubtitle}>
                  {t("components.paymentModal.cardSection3Description")}
                </p>
              </div>
              <div className={styles.cardPaymentSuccessContent}>
                <div className={styles.cardPaymentSuccessIconContainer}>
                  <PaymentSuccessIcon
                    className={styles.cardPaymentSuccessIcon}
                  />
                </div>
                {/* Payment Success Details Section */}
                <div className={styles.cardPaymentSuccessSection}>
                  <div className={styles.cardPaymentSuccessRow}>
                    <span className={styles.cardPaymentSuccessLabel}>
                      {t("components.paymentModal.cardSection3Label1")}
                    </span>
                  </div>
                  <div className={styles.cardPaymentSuccessRowCentered}>
                    <span className={styles.cardPaymentSuccessLabel2}>
                      {t("components.paymentModal.cardSection3Label2")}
                    </span>
                  </div>
                  <div className={styles.cardPaymentSuccessRowCentered}>
                    <span className={styles.cardPaymentSuccessValue}>
                      {event.nombre}
                    </span>
                  </div>
                  <div className={styles.cardPaymentDivider}></div>
                  <div className={styles.cardPaymentSuccessRow}>
                    <span className={styles.cardPaymentSuccessLabel2}>
                      {t("components.paymentModal.cardSection3Label3")}
                    </span>
                    <span className={styles.cardPaymentSuccessValue}>
                      ${getEventCost()} MXN
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

        {/* Step 3: Payment Error */}
        {stepNumber === 3 &&
          paymentMethod === "card" &&
          paymentStatus === "PAYMENT_ERROR" && (
            <div className={styles.stepContent}>
              <div className={styles.cardPaymentTitleContainer}>
                <div className={styles.cardPaymentTitle}>
                  {t("components.paymentModal.cardSection4Title")}
                </div>
                <p className={styles.cardPaymentSubtitle}>
                  {t("components.paymentModal.cardSection4Description")}
                </p>
              </div>
              <div className={styles.cardPaymentErrorContent}>
                <div className={styles.cardPaymentErrorIconContainer}>
                  <PaymentErrorIcon className={styles.cardPaymentErrorIcon} />
                </div>
                {/* Payment Error Details Section */}
                <div className={styles.cardPaymentErrorSection}>
                  <div className={styles.cardPaymentErrorRow}>
                    <span className={styles.cardPaymentErrorLabel}>
                      {t("components.paymentModal.cardSection4Label1")}
                    </span>
                  </div>
                  <div className={styles.cardPaymentErrorRowCentered}>
                    <span className={styles.cardPaymentErrorLabel2}>
                      {t("components.paymentModal.cardSection4Label2")}
                    </span>
                  </div>
                  <div className={styles.cardPaymentErrorRowCentered}>
                    <span className={styles.cardPaymentErrorValue}>
                      {event.nombre}
                    </span>
                  </div>
                  <div className={styles.cardPaymentDivider}></div>
                  <div className={styles.cardPaymentDetailRow}>
                    <span className={styles.cardPaymentErrorLabel}>
                      {t("components.paymentModal.cardSection4Label3")}
                    </span>
                    <span className={styles.cardPaymentErrorValue}>
                      ${getEventCost()} MXN
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

        {/* Filling out the business event registration information */}

        {/* The selected payment method is cash */}
        {stepNumber === 1 && paymentMethod === "cash" && (
          <div className={styles.formActions}>
            <div>
              <button
                className={styles.cancelButton}
                type="button"
                disabled={false}
                onClick={onClose}
              >
                {t("common.cancel")}
              </button>
              <button
                className={styles.nextButton}
                type="button"
                disabled={!isStep1Valid() || cashPaymentLoading}
                onClick={handleCashPayment}
              >
                {cashPaymentLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  t("common.pay")
                )}
              </button>
            </div>
          </div>
        )}

        {/* The selected payment method is card */}
        {stepNumber === 1 && paymentMethod === "card" && (
          <div className={styles.formActions}>
            <div>
              <button
                className={styles.previousButton}
                type="button"
                disabled={false}
                onClick={() => {
                  setStepNumber(stepNumber - 1);
                  setPaymentMethod("cash");
                }}
              >
                {t("common.previous")}
              </button>
              <button
                className={styles.nextButton}
                type="button"
                disabled={!isStep1Valid()}
                onClick={() => {
                  setStepNumber(stepNumber + 1);
                  if (isStep1Valid()) {
                    setStepNumber(stepNumber + 1);
                  }
                }}
              >
                {t("common.next")}
              </button>
            </div>
          </div>
        )}

        {/* The card payment details are being read */}
        {stepNumber === 2 && paymentMethod === "card" && (
          <div className={styles.formActions}>
            <div>
              <button
                className={styles.previousButton}
                type="button"
                disabled={false}
                onClick={() => {
                  setStepNumber(stepNumber - 1);
                  setPaymentMethod("card");
                }}
              >
                {t("common.cancel")}
              </button>
              <button
                className={styles.nextButton}
                type="button"
                disabled={!isStep2Valid() || cardPaymentLoading}
                onClick={handleCardPayment}
              >
                {cardPaymentLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  t("common.confirmPayment")
                )}
              </button>
            </div>
          </div>
        )}

        {/* The card payment was declined, the user can retry the payment */}
        {stepNumber === 3 &&
          paymentMethod === "card" &&
          paymentStatus === "PAYMENT_ERROR" && (
            <div className={styles.formActions}>
              <div>
                <button
                  className={styles.previousButton}
                  type="button"
                  disabled={false}
                  onClick={() => {
                    setStepNumber(stepNumber - 2);
                    setPaymentMethod("card");
                  }}
                >
                  {t("common.modifyData")}
                </button>
                <button
                  className={styles.nextButton}
                  type="button"
                  disabled={!isStep2Valid() || cardPaymentLoading}
                  onClick={handleCardPayment}
                >
                  {cardPaymentLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    t("common.retryPayment")
                  )}
                </button>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default AppointmentPaymentModal;
